/**
 * IndieChatFull — Full-page 3-pane chat layout
 * Phase 2: real conversation history, interactive context chips, resume from history
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Share2,
  Download,
  Maximize2,
  Minimize2,
  Lock,
  LockOpen,
  ChevronDown,
  Menu,
  X,
} from 'lucide-react';
import './orbit-chat.css';
import { ActionCard, ChatMessage, LeftTab, RightTab, SparkleIcon } from './indie-chat.types';
import IndieChatLeftSidebar from './IndieChatLeftSidebar';
import IndieChatRightPanel from './IndieChatRightPanel';
import IndieChatBody from './IndieChatBody';
import IndieChatInput from './IndieChatInput';
import GlobalTip from '../../providers/GlobalTip';
import { useOrbitChat, type OrbitChatViewHandoff } from '../../hooks/useOrbitChat';
import { useOrbitMaintenanceMessage } from '../../hooks/useOrbitMaintenanceMessage';
import { useOrbitMetadata } from '../../hooks/useOrbitMetadata';
import { useUserPrompts } from '../../hooks/useUserPrompts';
import { useOrbitFiles } from '../../hooks/useOrbitFiles';
import { useOrbitArtifacts } from '../../hooks/useOrbitArtifacts';
import { useOrbitContextOptions } from '../../hooks/useOrbitContextOptions';
import OrbitMaintenanceBanner from './OrbitMaintenanceBanner';
import {
  fetchOrbitConversations,
  generateOrbitActionCards,
  exportOrbitConversationPdf,
  updateOrbitConversationVisibility,
  OrbitClient,
  OrbitConversation,
  OrbitContextChips,
} from '../../services/OrbitChatService';
import { ORBIT_CONTEXT_FILE_ACCEPT, pickOrbitContextFiles, resolveOrbitContextUploadsFromFiles } from '../../services/OrbitChatUploadUtils';

const SidebarComponent = IndieChatLeftSidebar as React.ComponentType<any>;
const DEFAULT_LEFT_PANEL_WIDTH = 240;
const MIN_LEFT_PANEL_WIDTH = 240;
const DEFAULT_RIGHT_PANEL_WIDTH = 300;
const MIN_RIGHT_PANEL_WIDTH = 300;
const MAX_SIDE_PANEL_WIDTH = 480;

// ─── Interactive context chips ───────────────────────────────────────────────

interface ContextChipsProps {
  chips: OrbitContextChips;
  onChange: (chips: OrbitContextChips) => void;
  readOnly: boolean;
  periodOptions: Array<{ label: string; value: string | null }>;
  departmentOptions: Array<{ label: string; value: string | null }>;
  locationOptions: Array<{ label: string; value: string | null }>;
}

const ContextChips: React.FC<ContextChipsProps> = ({
  chips,
  onChange,
  readOnly,
  periodOptions,
  departmentOptions,
  locationOptions,
}) => {
  const periodLabel = chips.period ?? periodOptions[0]?.label ?? 'Latest period';
  const deptLabel = chips.department ?? 'All departments';
  const locLabel = chips.location ?? 'All locations';
  const [openChip, setOpenChip] = useState<keyof OrbitContextChips | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const chipButtonRefs = useRef<Partial<Record<keyof OrbitContextChips, HTMLButtonElement | null>>>({});
  const menuRef = useRef<HTMLDivElement | null>(null);
  const optionSets: Record<keyof OrbitContextChips, Array<{ label: string; value: string | null }>> = {
    period: periodOptions,
    department: departmentOptions,
    location: locationOptions,
  };

  const positionMenu = useCallback((key: keyof OrbitContextChips) => {
    const trigger = chipButtonRefs.current[key];
    if (!trigger) return;
    const bounds = trigger.getBoundingClientRect();
    setMenuPosition({ top: bounds.bottom + 4, left: bounds.left });
  }, []);

  useEffect(() => {
    if (!openChip) return undefined;

    const updatePosition = () => positionMenu(openChip);
    const closeIfClickedOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!chipButtonRefs.current[openChip]?.contains(target) && !menuRef.current?.contains(target)) {
        setOpenChip(null);
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    document.addEventListener('mousedown', closeIfClickedOutside);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      document.removeEventListener('mousedown', closeIfClickedOutside);
    };
  }, [openChip, positionMenu]);

  const chip = (
    label: string,
    icon: React.ReactNode,
    options: Array<{ label: string; value: string | null }>,
    key: keyof OrbitContextChips,
    extraClass = '',
  ) =>
    <div key={key} className={`relative ${extraClass}`}>
      <button
        type="button"
        ref={(element) => { chipButtonRefs.current[key] = element; }}
        aria-haspopup="menu"
        aria-expanded={openChip === key}
        disabled={readOnly}
        onClick={() => {
          if (openChip === key) {
            setOpenChip(null);
            return;
          }
          positionMenu(key);
          setOpenChip(key);
        }}
        className="flex items-center gap-1 px-[9px] py-[4.5px] rounded-[8px] border border-[#DDD6FE] bg-[#F9F5FF] hover:border-[#9E77ED] hover:bg-[#F4EBFF] transition-colors disabled:cursor-not-allowed disabled:opacity-60"
      >
        {icon}
        <span className="text-[#53389E] text-[10px] font-medium leading-[15px] tracking-[0.117px]">{label}</span>
        <ChevronDown className="w-2.5 h-2.5 text-[#98A2B3] ml-0.5" />
      </button>
    </div>;

  return (
    <>
      <div className="hidden sm:flex items-center gap-[5px]">
        {chip(
          periodLabel,
          <div className="w-1.5 h-1.5 rounded-full bg-[#7F56D9] animate-pulse-dot flex-shrink-0" />,
          periodOptions,
          'period',
        )}
        {chip(
          deptLabel,
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M3.75 1.25H1.66667C1.43655 1.25 1.25 1.43655 1.25 1.66667V4.58333C1.25 4.81345 1.43655 5 1.66667 5H3.75C3.98012 5 4.16667 4.81345 4.16667 4.58333V1.66667C4.16667 1.43655 3.98012 1.25 3.75 1.25Z" stroke="#53389E" strokeWidth="0.833333" strokeLinecap="round" strokeLinejoin="round" /><path d="M8.33334 1.25H6.25001C6.01989 1.25 5.83334 1.43655 5.83334 1.66667V2.91667C5.83334 3.14679 6.01989 3.33333 6.25001 3.33333H8.33334C8.56346 3.33333 8.75001 3.14679 8.75001 2.91667V1.66667C8.75001 1.43655 8.56346 1.25 8.33334 1.25Z" stroke="#53389E" strokeWidth="0.833333" strokeLinecap="round" strokeLinejoin="round" /><path d="M8.33334 5H6.25001C6.01989 5 5.83334 5.18655 5.83334 5.41667V8.33333C5.83334 8.56345 6.01989 8.75 6.25001 8.75H8.33334C8.56346 8.75008 8.75001 8.56353 8.75001 8.33341V5.41667C8.75001 5.18655 8.56346 5 8.33334 5Z" stroke="#53389E" strokeWidth="0.833333" strokeLinecap="round" strokeLinejoin="round" /><path d="M3.75 6.66675H1.66667C1.43655 6.66675 1.25 6.8533 1.25 7.08341V8.33341C1.25 8.56353 1.43655 8.75008 1.66667 8.75008H3.75C3.98012 8.75008 4.16667 8.56353 4.16667 8.33341V7.08341C4.16667 6.8533 3.98012 6.66675 3.75 6.66675Z" stroke="#53389E" strokeWidth="0.833333" strokeLinecap="round" strokeLinejoin="round" /></svg>,
          departmentOptions,
          'department',
          'hidden md:block',
        )}
        {chip(
          locLabel,
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M8.33335 4.16659C8.33335 6.247 6.02544 8.41367 5.25044 9.08284C5.17824 9.13712 5.09035 9.16648 5.00002 9.16648C4.90969 9.16648 4.8218 9.13712 4.7496 9.08284C3.9746 8.41367 1.66669 6.247 1.66669 4.16659C1.66669 3.28253 2.01788 2.43468 2.643 1.80956C3.26812 1.18444 4.11597 0.833252 5.00002 0.833252C5.88408 0.833252 6.73192 1.18444 7.35704 1.80956C7.98216 2.43468 8.33335 3.28253 8.33335 4.16659Z" stroke="#53389E" strokeWidth="0.833333" strokeLinecap="round" strokeLinejoin="round" /><path d="M5 5.41675C5.69036 5.41675 6.25 4.8571 6.25 4.16675C6.25 3.47639 5.69036 2.91675 5 2.91675C4.30964 2.91675 3.75 3.47639 3.75 4.16675C3.25 4.8571 4.30964 5.41675 5 5.41675Z" stroke="#53389E" strokeWidth="0.833333" strokeLinecap="round" strokeLinejoin="round" /></svg>,
          locationOptions,
          'location',
          'hidden lg:block',
        )}
      </div>
      {openChip && !readOnly && typeof document !== 'undefined' && createPortal(
        <div ref={menuRef} role="menu" className="fixed z-[1000] flex min-w-[160px] flex-col rounded-[8px] border border-[#EAECF0] bg-white py-1 shadow-lg" style={{ top: menuPosition.top, left: menuPosition.left }}>
          {optionSets[openChip].map((opt) => (
            <button type="button" role="menuitem" key={`${openChip}-${opt.label}`} onClick={() => { onChange({ ...chips, [openChip]: opt.value }); setOpenChip(null); }} className="px-3 py-1.5 text-left text-[11px] text-[#344054] hover:bg-[#F9F5FF]">
              {opt.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

interface IndieChatFullProps {
  title?: string;
  clientId?: number;
  client?: OrbitClient;
  initialConversationId?: string;
  initialPrompt?: string;
  initialHandoff?: OrbitChatViewHandoff;
  onConversationIdChange?: (conversationId?: string) => void;
  leftSidebarComponent?: React.ComponentType<any>;
  rightPanelComponent?: React.ComponentType<any>;
  userName?: string;
}

const IndieChatFull: React.FC<IndieChatFullProps> = ({
  title = 'Opportunities & Issues',
  clientId = 0,
  client = {},
  initialConversationId,
  initialPrompt,
  initialHandoff,
  onConversationIdChange,
  leftSidebarComponent,
  rightPanelComponent,
  userName,
}) => {
  const tipContext = GlobalTip.useMessage();
  const [leftTab, setLeftTab] = useState<LeftTab>('history');
  const [rightTab, setRightTab] = useState<RightTab>('artifacts');
  const [showLeftSidebar, setShowLeftSidebar] = useState(false);
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [isCenterChatMaximized, setIsCenterChatMaximized] = useState(false);
  const [isVisibilityMenuOpen, setIsVisibilityMenuOpen] = useState(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(DEFAULT_LEFT_PANEL_WIDTH);
  const [rightPanelWidth, setRightPanelWidth] = useState(DEFAULT_RIGHT_PANEL_WIDTH);
  const [resizingPanel, setResizingPanel] = useState<'left' | 'right' | null>(null);
  const [resumeConvId, setResumeConvId] = useState<string | undefined>(initialConversationId);
  const [visibleActionCards, setVisibleActionCards] = useState<ActionCard[]>([]);
  const [refreshingActionCards, setRefreshingActionCards] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const resizeStartRef = useRef<{ panel: 'left' | 'right'; clientX: number; width: number } | null>(null);

  // Phase 2: conversation history
  const [conversations, setConversations] = useState<OrbitConversation[]>([]);
  const [convsLoading, setConvsLoading] = useState(false);

  // Phase 3: DB-backed prompts + action cards (includes per-client prompts)
  const { m1Prompts, clientPrompts, actionCards, refreshingCards, refreshActionCards } = useOrbitMetadata(clientId);

  // Phase D: user-saved "My Prompts"
  const { prompts: userPrompts, addPrompt, deletePrompt } = useUserPrompts(clientId);

  const {
    messages,
    isStreaming,
    streamingMessageId,
    sendMessage,
    conversationId,
    quotaExceeded,
    submitFeedback,
    contextChips,
    setContextChips,
    isLoadingHistory,
    contextUploads,
    addContextUploads,
    removeContextUpload,
    resetConversation,
  } = useOrbitChat({ clientId, client, initialConversationId: resumeConvId, initialHandoff });
  const { banner: maintenanceBanner } = useOrbitMaintenanceMessage(clientId);
  const maintenanceModeActive = Boolean(
    maintenanceBanner.is_enabled && String(maintenanceBanner.message || '').trim(),
  );
  useEffect(() => {
    setResumeConvId(initialConversationId);
  }, [initialConversationId]);

  useEffect(() => {
    onConversationIdChange?.(conversationId);
  }, [conversationId, onConversationIdChange]);

  const { files, loading: filesLoading, uploading, uploadPolicy, uploadFile, deleteFile, downloadFile, getContextUpload, refresh: refreshFiles } = useOrbitFiles(clientId, conversationId);
  const { artifacts, loading: artifactsLoading, saveArtifact, updateArtifact, deleteArtifact } = useOrbitArtifacts(clientId);
  const { periodOptions, departmentOptions, locationOptions } = useOrbitContextOptions(clientId, client);
  const LeftSidebar = leftSidebarComponent ?? SidebarComponent;
  const RightPanel = rightPanelComponent ?? IndieChatRightPanel;

  useEffect(() => {
    setVisibleActionCards(actionCards);
  }, [actionCards]);

  const clampSidePanelWidth = useCallback((panel: 'left' | 'right', width: number) => {
    const minWidth = panel === 'left' ? MIN_LEFT_PANEL_WIDTH : MIN_RIGHT_PANEL_WIDTH;
    const viewportMax = typeof window === 'undefined'
      ? MAX_SIDE_PANEL_WIDTH
      : Math.floor(window.innerWidth * 0.45);
    return Math.max(minWidth, Math.min(MAX_SIDE_PANEL_WIDTH, viewportMax, Math.round(width)));
  }, []);

  const startPanelResize = useCallback((panel: 'left' | 'right', clientX: number) => {
    if (!Number.isFinite(clientX)) return;
    resizeStartRef.current = {
      panel,
      clientX,
      width: panel === 'left' ? leftPanelWidth : rightPanelWidth,
    };
    setResizingPanel(panel);
  }, [leftPanelWidth, rightPanelWidth]);

  useEffect(() => {
    if (!resizingPanel) return undefined;

    const updatePanelWidth = (event: MouseEvent) => {
      const resizeStart = resizeStartRef.current;
      if (!resizeStart || !Number.isFinite(event.clientX)) return;
      const delta = event.clientX - resizeStart.clientX;
      const nextWidth = clampSidePanelWidth(
        resizeStart.panel,
        resizeStart.panel === 'left' ? resizeStart.width + delta : resizeStart.width - delta,
      );
      if (resizeStart.panel === 'left') {
        setLeftPanelWidth(nextWidth);
      } else {
        setRightPanelWidth(nextWidth);
      }
    };
    const stopPanelResize = () => {
      resizeStartRef.current = null;
      setResizingPanel(null);
    };

    document.body.classList.add('select-none', 'cursor-col-resize');
    window.addEventListener('mousemove', updatePanelWidth);
    window.addEventListener('mouseup', stopPanelResize);
    return () => {
      document.body.classList.remove('select-none', 'cursor-col-resize');
      window.removeEventListener('mousemove', updatePanelWidth);
      window.removeEventListener('mouseup', stopPanelResize);
    };
  }, [clampSidePanelWidth, resizingPanel]);

  const handlePanelResizeKeyDown = useCallback((panel: 'left' | 'right', event: React.KeyboardEvent<HTMLDivElement>) => {
    const direction = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!direction) return;
    event.preventDefault();
    const increment = event.shiftKey ? 40 : 10;
    const delta = panel === 'left' ? direction * increment : -direction * increment;
    if (panel === 'left') {
      setLeftPanelWidth((width) => clampSidePanelWidth('left', width + delta));
    } else {
      setRightPanelWidth((width) => clampSidePanelWidth('right', width + delta));
    }
  }, [clampSidePanelWidth]);

  // Fetch conversation history on mount and after each completed stream
  const loadConversations = useCallback(async () => {
    if (!clientId) return;
    setConvsLoading(true);
    try {
      const list = await fetchOrbitConversations(clientId, 20);
      setConversations(list);
    } finally {
      setConvsLoading(false);
    }
  }, [clientId]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // Refresh history list after streaming completes and retry briefly so newly
  // persisted conversations survive fast navigation / slower DB commit windows.
  useEffect(() => {
    if (!isStreaming && messages.length > 0) {
      let cancelled = false;
      const timeouts: ReturnType<typeof setTimeout>[] = [];
      const activeConversationId = conversationId;

      const scheduleAttempt = (delayMs: number) => {
        const timeoutId = setTimeout(async () => {
          if (cancelled) return;

          try {
            const list = await fetchOrbitConversations(clientId, 20);
            if (cancelled) return;

            setConversations(list);

            if (!activeConversationId) {
              return;
            }

            const found = list.some((conversation) => conversation.conversation_id === activeConversationId);
            if (found) {
              cancelled = true;
            }
          } catch {
            // Keep the existing sidebar state and let later retries handle it.
          }
        }, delayMs);

        timeouts.push(timeoutId);
      };

      [250, 800, 1600].forEach(scheduleAttempt);

      return () => {
        cancelled = true;
        timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      };
    }
  }, [clientId, conversationId, isStreaming, messages.length]);

  const handleSelectConversation = useCallback((conv: OrbitConversation) => {
    if (maintenanceModeActive) {
      tipContext.show('Conversation history is temporarily unavailable while Ask Indie maintenance is enabled.');
      return;
    }

    setResumeConvId(conv.conversation_id);
    setShowLeftSidebar(false);
  }, [maintenanceModeActive, tipContext]);

  const handleNewConversation = useCallback(() => {
    setResumeConvId(undefined);
    resetConversation();
    setShowLeftSidebar(false);
  }, [resetConversation]);

  const handleConversationVisibilityChange = useCallback(async (
    conversation: OrbitConversation,
    conversationVisibility: 'private' | 'shared',
  ) => {
    try {
      await updateOrbitConversationVisibility(clientId, conversation.conversation_id, conversationVisibility);
      await loadConversations();
      tipContext.showSuccess(
        conversationVisibility === 'shared'
          ? 'Conversation is now shared with this client team'
          : 'Conversation is now private',
      );
    } catch (error: any) {
      tipContext.show(error?.message || 'Unable to update conversation privacy right now.');
    }
  }, [clientId, loadConversations, tipContext]);

  const sendAskIndieMessage = useCallback((text: string) => {
    if (maintenanceModeActive) {
      tipContext.show('Ask Indie is temporarily unavailable while maintenance is enabled.');
      return;
    }

    sendMessage(text);
  }, [maintenanceModeActive, sendMessage, tipContext]);

  const handlePromptSelect = useCallback((text: string) => {
    sendAskIndieMessage(text);
    setLeftTab('history');
    setShowLeftSidebar(false);
  }, [sendAskIndieMessage]);

  const handleActionSelect = useCallback((text: string) => {
    sendAskIndieMessage(text);
    setShowRightPanel(false);
  }, [sendAskIndieMessage]);

  const handleOpenStoredFilePicker = useCallback(() => {
    if (uploadPolicy?.files_remaining_this_month === 0) {
      tipContext.show(`${uploadPolicy.tier} tier upload limit reached: ${uploadPolicy.max_files_per_month} files per month.`);
      return;
    }
    setLeftTab('files');
    setShowLeftSidebar(true);
    fileInputRef.current?.click();
  }, [tipContext, uploadPolicy]);

  const handleAttachContextFilesToChat = useCallback(async () => {
    const selectedFiles = await pickOrbitContextFiles();
    if (selectedFiles.length === 0) {
      return;
    }

    const { uploads, storedUploadCount, errors } = await resolveOrbitContextUploadsFromFiles(selectedFiles, {
      clientId: clientId && clientId > 0 ? clientId : undefined,
      conversationId,
    });

    if (uploads.length > 0) {
      addContextUploads(uploads);
      setLeftTab('files');
      setShowLeftSidebar(true);
      if (storedUploadCount > 0) {
        refreshFiles();
      }
      tipContext.showSuccess(
        uploads.length === 1
          ? 'File added to the current chat context'
          : `${uploads.length} files added to the current chat context`,
      );
    }

    if (errors.length > 0) {
      tipContext.show(errors[0]);
    }
  }, [addContextUploads, clientId, conversationId, refreshFiles, tipContext]);

  const handleFilePicked = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;
    try {
      const [firstFile] = selectedFiles;
      if (firstFile) {
        await uploadFile(firstFile);
        setLeftTab('files');
      }
    } catch (error: any) {
      tipContext.show(error?.message || 'Unable to upload that file right now.');
    } finally {
      event.target.value = '';
    }
  }, [tipContext, uploadFile]);

  const handleDownloadFile = useCallback(async (file: { id: number; original_filename: string }) => {
    await downloadFile(file.id, file.original_filename);
  }, [downloadFile]);

  const contextFileIds = useMemo(() => contextUploads
    .map((upload) => {
      const orbitFileId = upload?.metadata && typeof upload.metadata === 'object'
        ? (upload.metadata as Record<string, unknown>).orbit_file_id
        : null;
      return typeof orbitFileId === 'number' ? orbitFileId : null;
    })
    .filter((orbitFileId): orbitFileId is number => orbitFileId !== null), [contextUploads]);

  const handleIncludeStoredFileInContext = useCallback(async (file: { id: number; original_filename: string }) => {
    try {
      const upload = await getContextUpload(file.id);
      const alreadyIncluded = contextUploads.some((existingUpload) => {
        const orbitFileId = existingUpload?.metadata && typeof existingUpload.metadata === 'object'
          ? (existingUpload.metadata as Record<string, unknown>).orbit_file_id
          : null;
        return orbitFileId === file.id;
      });
      if (alreadyIncluded) {
        tipContext.show('This file is already included in the current chat context.');
        return;
      }
      addContextUploads([upload]);
      tipContext.showSuccess('File added to the current chat context');
    } catch (error: any) {
      tipContext.show(error?.message || 'Unable to add that file to the current chat context right now.');
    }
  }, [addContextUploads, contextUploads, getContextUpload, tipContext]);

  const formatSavedLabel = useCallback((content: string, prefix = 'Saved prompt') => {
    const singleLine = content.replace(/\s+/g, ' ').trim();
    if (!singleLine) return prefix;
    return singleLine.length > 60 ? `${singleLine.slice(0, 57)}…` : singleLine;
  }, []);

  const handleSavePrompt = useCallback(async (message: ChatMessage) => {
    try {
      await addPrompt({
        label: formatSavedLabel(message.content, message.role === 'assistant' ? 'Assistant prompt' : 'My prompt'),
        promptText: message.content,
      });
      setLeftTab('prompts');
      tipContext.showSuccess('Saved to My Prompts');
    } catch {
      tipContext.show('Unable to save this prompt right now.');
    }
  }, [addPrompt, formatSavedLabel, tipContext]);

  const handleSaveArtifact = useCallback(async (message: ChatMessage) => {
    try {
      await saveArtifact({
        title: formatSavedLabel(message.content, 'Saved analysis'),
        artifact_type: 'summary',
        content_markdown: message.content,
        conversation_id: conversationId,
        source_message_id: Number(String(message.id).replace(/^hist-/, '')) || undefined,
      });
      setRightTab('artifacts');
      setShowRightPanel(true);
      tipContext.showSuccess('Artifact saved');
    } catch (err) {
      tipContext.show('Unable to save this artifact right now.');
      throw err;
    }
  }, [conversationId, formatSavedLabel, saveArtifact, tipContext]);

  const handleDownloadArtifact = useCallback((artifact: { title: string; content_markdown: string }) => {
    const blob = new Blob([artifact.content_markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${artifact.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'artifact'}.md`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, []);

  const handleExportConversation = useCallback(async () => {
    if (!conversationId || messages.length === 0) {
      tipContext.show('Start a conversation before exporting it.');
      return;
    }

    try {
      // SPS-5637: rendered server-side so markdown tables/bold/headings come
      // out formatted instead of as literal |, ** and #.
      // Times are stored in UTC; tell the server which zone the merchant is in
      // so the PDF shows their clock, not UTC-as-local.
      let timeZone: string | undefined;
      try {
        timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
      } catch {
        timeZone = undefined;
      }
      const blob = await exportOrbitConversationPdf(clientId, conversationId, timeZone);
      const filenameBase = `${title}-${conversationId}`.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${filenameBase || 'ask-indie-conversation'}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
      tipContext.showSuccess('Conversation download started');
    } catch (err) {
      tipContext.show('Unable to export this conversation right now.');
    }
  }, [clientId, conversationId, messages, tipContext, title]);

  const handleRefreshActionCards = useCallback(async () => {
    if (!clientId) return;
    setRefreshingActionCards(true);
    try {
      const generated = await generateOrbitActionCards(clientId, client, {
        conversationId,
        context: {
          ...(contextChips.period ? { period: contextChips.period } : {}),
          ...(contextChips.department ? { department: contextChips.department } : {}),
          ...(contextChips.location ? { location: contextChips.location } : {}),
        },
        recentMessages: messages.slice(-6).map((message) => `${message.role}: ${message.content}`),
      });
      if (generated.length > 0) {
        setVisibleActionCards(generated);
        return;
      }
      await refreshActionCards();
    } finally {
      setRefreshingActionCards(false);
    }
  }, [clientId, client, contextChips.department, contextChips.location, contextChips.period, conversationId, messages, refreshActionCards]);

  const hasMessages = messages.length > 0;

  const periodChipOptions = useMemo(
    () => [{ label: 'Latest period', value: null }, ...periodOptions.map((period) => ({ label: period, value: period }))],
    [periodOptions],
  );
  const departmentChipOptions = useMemo(
    () => [{ label: 'All departments', value: null }, ...departmentOptions.map((department) => ({ label: department, value: department }))],
    [departmentOptions],
  );
  const locationChipOptions = useMemo(
    () => [{ label: 'All locations', value: null }, ...locationOptions.map((location) => ({ label: location, value: location }))],
    [locationOptions],
  );

  // Merge the current session into the top of the history list so the user
  // always sees it even before the API has confirmed the DB write.
  const effectiveConversations = React.useMemo<OrbitConversation[]>(() => {
    const savedConversations = Array.isArray(conversations) ? conversations : [];
    if (!conversationId || !hasMessages) return savedConversations;
    const alreadySaved = savedConversations.some((c) => c.conversation_id === conversationId);
    if (alreadySaved) return savedConversations;
    const firstUserMsg = messages.find((m) => m.role === 'user')?.content ?? null;
    const syntheticEntry: OrbitConversation = {
      conversation_id: conversationId,
      status: 'active',
      first_user_message: firstUserMsg,
      last_message_at: null,
      created_at: new Date().toISOString(),
    };
    return [syntheticEntry, ...savedConversations];
  }, [conversations, conversationId, hasMessages, messages]);

  const activeConversation = useMemo(
    () => effectiveConversations.find((conversation) => conversation.conversation_id === conversationId),
    [conversationId, effectiveConversations],
  );
  const canManageActiveConversation = Boolean(activeConversation && conversationId);

  return (
    <div className="indie-chat flex h-full flex-col bg-[#F2F4F7] overflow-hidden">
      {/* Full-page toolbar — spans across top of entire Ask Indie surface */}
      <div className="hidden lg:flex min-h-[52px] items-center border-b border-[#EAECF0] bg-white px-4 py-2 flex-shrink-0 z-10">
        <div className="flex shrink-0 items-center gap-[9px]" style={{ width: Math.max(leftPanelWidth - 16, 224) }}>
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#EEEDFE]">
            <SparkleIcon />
          </div>
          <div className="flex flex-col">
            <span className="text-[14px] font-semibold leading-tight text-[#101828]">Ask Indie</span>
            <span className="text-[11px] leading-tight text-[#98A2B3]">40 years of M1 expertise</span>
          </div>
        </div>
        <div className="h-4 w-px shrink-0 bg-[#EAECF0] mx-2" />
        <div className="flex shrink-0 items-center gap-1.5">
          <span className="max-w-[320px] truncate text-[14px] font-medium text-[#101828]">{title}</span>
        </div>
        <div className="h-4 w-px shrink-0 bg-[#EAECF0] mx-2 hidden sm:block" />
        <ContextChips
          chips={contextChips}
          onChange={setContextChips}
          readOnly={isStreaming}
          periodOptions={periodChipOptions}
          departmentOptions={departmentChipOptions}
          locationOptions={locationChipOptions}
        // classOptions={classChipOptions}
        />
        <div className="min-w-0 flex-1" />
        <div className="ml-auto flex shrink-0 items-center gap-0.5">
          <div className="relative">
            <button
              type="button"
              aria-label="Share conversation"
              aria-haspopup="menu"
              aria-expanded={isVisibilityMenuOpen}
              disabled={!canManageActiveConversation}
              title={canManageActiveConversation ? 'Set conversation privacy' : 'Start a conversation to set its privacy'}
              onClick={() => setIsVisibilityMenuOpen((isOpen) => !isOpen)}
              className="flex h-7 w-7 items-center justify-center rounded-md text-[#98A2B3] hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Share2 className="h-[13px] w-[13px]" />
            </button>
            {isVisibilityMenuOpen && activeConversation && (
              <div role="menu" aria-label="Conversation privacy" className="absolute right-0 top-8 z-20 w-64 rounded-lg border border-[#EAECF0] bg-white p-1 shadow-lg">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setIsVisibilityMenuOpen(false);
                    void handleConversationVisibilityChange(activeConversation, 'private');
                  }}
                  className="flex w-full items-start gap-2 rounded-md px-2 py-2 text-left hover:bg-[#F9FAFB]"
                >
                  <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#667085]" />
                  <span><strong className="block text-[11px] text-[#101828]">Private</strong><span className="block text-[10px] text-[#667085]">Only you can access this chat</span></span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setIsVisibilityMenuOpen(false);
                    void handleConversationVisibilityChange(activeConversation, 'shared');
                  }}
                  className="flex w-full items-start gap-2 rounded-md px-2 py-2 text-left hover:bg-[#F9FAFB]"
                >
                  <LockOpen className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#667085]" />
                  <span><strong className="block text-[11px] text-[#101828]">Public</strong><span className="block text-[10px] text-[#667085]">Shared with authorized client users</span></span>
                </button>
              </div>
            )}
          </div>
          <button type="button" aria-label="Export conversation" disabled={!hasMessages} title={hasMessages ? 'Download conversation as PDF' : 'Start a conversation to export it'} onClick={handleExportConversation} className="flex h-7 w-7 items-center justify-center rounded-md text-[#98A2B3] hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-50"><Download className="h-[13px] w-[13px]" /></button>
          <div className="mx-0.5 h-3.5 w-px bg-[#EAECF0]" />
          <button type="button" aria-label={isCenterChatMaximized ? 'Restore chat panels' : 'Maximize chat'} title={isCenterChatMaximized ? 'Restore chat panels' : 'Maximize chat'} onClick={() => setIsCenterChatMaximized((isMaximized) => !isMaximized)} className="flex h-7 w-7 items-center justify-center rounded-md text-[#98A2B3] hover:bg-[#F9FAFB]">
            {isCenterChatMaximized ? <Minimize2 className="h-[13px] w-[13px]" /> : <Maximize2 className="h-[13px] w-[13px]" />}
          </button>
        </div>
      </div>

      <div className="flex-1 flex min-h-0 overflow-hidden relative">
        {/* ── Mobile sidebar overlays ── */}
        {showLeftSidebar && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div className="absolute inset-0 bg-black/30" onClick={() => setShowLeftSidebar(false)} />
            <div className="absolute left-0 top-0 h-full z-50 shadow-xl">
              <div className="relative">
                <button onClick={() => setShowLeftSidebar(false)} className="absolute top-2 right-2 z-10 p-1 rounded-md hover:bg-gray-100">
                  <X className="w-4 h-4 text-[#667085]" />
                </button>
                {React.createElement(LeftSidebar, {
                  activeTab: leftTab,
                  onTabChange: setLeftTab,
                  conversations: effectiveConversations,
                  conversationsLoading: convsLoading,
                  onSelectConversation: handleSelectConversation,
                  onConversationVisibilityChange: handleConversationVisibilityChange,
                  onNewConversation: handleNewConversation,
                  activeConversationId: conversationId,
                  historyDisabled: maintenanceModeActive,
                  m1Prompts,
                  clientPrompts,
                  onPromptSelect: handlePromptSelect,
                  promptsDisabled: maintenanceModeActive,
                  userPrompts,
                  onAddUserPrompt: addPrompt,
                  onDeleteUserPrompt: deletePrompt,
                  files,
                  filesLoading,
                  uploadingFile: uploading,
                  uploadPolicy,
                  onOpenFilePicker: handleOpenStoredFilePicker,
                  onDeleteFile: deleteFile,
                  onDownloadFile: handleDownloadFile,
                  onIncludeFileInContext: handleIncludeStoredFileInContext,
                  contextFileIds,
                })}
              </div>
            </div>
          </div>
        )}
        {showRightPanel && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div className="absolute inset-0 bg-black/30" onClick={() => setShowRightPanel(false)} />
            <div className="absolute right-0 top-0 h-full z-50 shadow-xl">
              <div className="relative">
                <button onClick={() => setShowRightPanel(false)} className="absolute top-2 left-2 z-10 p-1 rounded-md hover:bg-gray-100">
                  <X className="w-4 h-4 text-[#667085]" />
                </button>
                <RightPanel
                  activeTab={rightTab}
                  onTabChange={setRightTab}
                  actionCards={visibleActionCards}
                  onActionSelect={handleActionSelect}
                  onRefresh={handleRefreshActionCards}
                  refreshing={refreshingActionCards || refreshingCards}
                  artifacts={artifacts}
                  artifactsLoading={artifactsLoading}
                  onArtifactUpdate={updateArtifact}
                  onArtifactDownload={handleDownloadArtifact}
                  onArtifactDelete={(artifact) => void deleteArtifact(artifact.id)}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Desktop left sidebar ── */}
        <div className={isCenterChatMaximized ? 'hidden' : 'hidden lg:block shrink-0 [&>*]:!w-full'} style={{ width: leftPanelWidth }}>
          {React.createElement(LeftSidebar, {
            activeTab: leftTab,
            onTabChange: setLeftTab,
            conversations: effectiveConversations,
            conversationsLoading: convsLoading,
            onSelectConversation: handleSelectConversation,
            onConversationVisibilityChange: handleConversationVisibilityChange,
            onNewConversation: handleNewConversation,
            activeConversationId: conversationId,
            historyDisabled: maintenanceModeActive,
            m1Prompts,
            clientPrompts,
            onPromptSelect: handlePromptSelect,
            promptsDisabled: maintenanceModeActive,
            userPrompts,
            onAddUserPrompt: addPrompt,
            onDeleteUserPrompt: deletePrompt,
            files,
            filesLoading,
            uploadingFile: uploading,
            uploadPolicy,
            onOpenFilePicker: handleOpenStoredFilePicker,
            onDeleteFile: deleteFile,
            onDownloadFile: handleDownloadFile,
            onIncludeFileInContext: handleIncludeStoredFileInContext,
            contextFileIds,
          })}
        </div>
        {!isCenterChatMaximized && (
          <div
            role="separator"
            aria-label="Resize history and resources panel"
            aria-orientation="vertical"
            aria-valuemin={MIN_LEFT_PANEL_WIDTH}
            aria-valuemax={MAX_SIDE_PANEL_WIDTH}
            aria-valuenow={leftPanelWidth}
            tabIndex={0}
            onMouseDown={(event) => {
              event.preventDefault();
              startPanelResize('left', event.clientX);
            }}
            onKeyDown={(event) => handlePanelResizeKeyDown('left', event)}
            className={`hidden w-1 shrink-0 cursor-col-resize bg-transparent transition-colors lg:block hover:bg-[#C3BFFA] focus:bg-[#C3BFFA] focus:outline-none ${resizingPanel === 'left' ? 'bg-[#9E77ED]' : ''}`}
          />
        )}

        {/* ── Center: mobile top bar + chat panel ── */}
        <div className="flex-1 flex flex-col min-w-0 relative">

          {/* Mobile top bar */}
          <div className="lg:hidden flex items-center justify-between px-4 h-12 bg-white border-b border-[#EAECF0] flex-shrink-0">
            <button onClick={() => setShowLeftSidebar(true)} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors">
              <Menu className="w-5 h-5 text-[#667085]" />
            </button>
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-md flex items-center justify-center bg-[#EEEDFE]">
                <SparkleIcon />
              </div>
              <span className="text-[#101828] text-sm font-semibold">Ask Indie</span>
            </div>
            <button onClick={() => setShowRightPanel(true)} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors">
              <svg width="18" height="18" viewBox="0 0 13 13" fill="none"><g clipPath="url(#mob)"><path d="M8.12496 1.08325H3.24996C2.96264 1.08325 2.68709 1.19739 2.48393 1.40055C2.28076 1.60372 2.16663 1.87927 2.16663 2.16659V10.8333C2.16663 11.1206 2.28076 11.3961 2.48393 11.5993C2.68709 11.8024 2.96264 11.9166 3.24996 11.9166H9.74996C10.0373 11.9166 10.3128 11.8024 10.516 11.5993C10.7192 11.3961 10.8333 11.1206 10.8333 10.8333V3.79159L8.12496 1.08325Z" stroke="#667085" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" /><path d="M7.58337 1.08325V3.24992C7.58337 3.53724 7.69751 3.81279 7.90068 4.01595C8.10384 4.21912 8.37939 4.33325 8.66671 4.33325H10.8334" stroke="#667085" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" /></g><defs><clipPath id="mob"><rect width="13" height="13" fill="white" /></clipPath></defs></svg>
            </button>
          </div>

          {maintenanceBanner.is_enabled && maintenanceBanner.message ? (
            <OrbitMaintenanceBanner message={maintenanceBanner.message} />
          ) : null}

          {/* Body + Input */}
          <IndieChatBody
            messages={messages}
            onSuggestionClick={sendAskIndieMessage}
            streamingMessageId={streamingMessageId}
            isStreaming={isStreaming}
            isLoadingHistory={isLoadingHistory}
            conversationId={conversationId}
            quotaExceeded={quotaExceeded}
            onFeedback={submitFeedback}
            onSavePrompt={handleSavePrompt}
            onSaveArtifact={handleSaveArtifact}
            artifacts={artifacts}
            userName={userName}
            hideStartPrompts={maintenanceModeActive}
            disableSuggestionClicks={maintenanceModeActive}
          />
          <IndieChatInput
            onSend={sendAskIndieMessage}
            onAttachFile={handleAttachContextFilesToChat}
            attachedUploads={contextUploads}
            onRemoveUpload={removeContextUpload}
            disabled={maintenanceModeActive}
            isStreaming={isStreaming || isLoadingHistory}
            initialValue={initialPrompt}
          />
        </div>

        {/* ── Desktop right panel ── */}
        {!isCenterChatMaximized && (
          <div
            role="separator"
            aria-label="Resize actions and artifacts panel"
            aria-orientation="vertical"
            aria-valuemin={MIN_RIGHT_PANEL_WIDTH}
            aria-valuemax={MAX_SIDE_PANEL_WIDTH}
            aria-valuenow={rightPanelWidth}
            tabIndex={0}
            onMouseDown={(event) => {
              event.preventDefault();
              startPanelResize('right', event.clientX);
            }}
            onKeyDown={(event) => handlePanelResizeKeyDown('right', event)}
            className={`hidden w-1 shrink-0 cursor-col-resize bg-transparent transition-colors lg:block hover:bg-[#C3BFFA] focus:bg-[#C3BFFA] focus:outline-none ${resizingPanel === 'right' ? 'bg-[#9E77ED]' : ''}`}
          />
        )}
        <div className={isCenterChatMaximized ? 'hidden' : 'hidden lg:block shrink-0 [&>*]:!w-full'} style={{ width: rightPanelWidth }}>
          <RightPanel
            activeTab={rightTab}
            onTabChange={setRightTab}
            actionCards={visibleActionCards}
            onActionSelect={handleActionSelect}
            onRefresh={handleRefreshActionCards}
            refreshing={refreshingActionCards || refreshingCards}
            artifacts={artifacts}
            artifactsLoading={artifactsLoading}
            onArtifactUpdate={updateArtifact}
            onArtifactDownload={handleDownloadArtifact}
            onArtifactDelete={(artifact) => void deleteArtifact(artifact.id)}
          />
        </div>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={ORBIT_CONTEXT_FILE_ACCEPT}
          onChange={handleFilePicked}
        />
      </div>
    </div>
  );
};

export default IndieChatFull;